import { Loader2 } from "lucide-react";
import { createPortal } from "react-dom";
import ConfirmModal from "../../../../../components/confirmViewModal/ConfirmModal";
import TailwindBottomButtonView from "../../../../../components/mainBottomButtonView/TailwindBottomButtonView";
import { Form } from "../../../../../components/ui/common/form";
import CommonSplitContainer from "../../../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import FacilityRegisterModal from "../../../../facility/facilityRegister/FacilityRegisterModal";
import ExternalStorageFacilityViewModal from "./facility/ExternalStorageFacilityViewModal";
import ExternalStorageDetailsSection from "./components/ExternalStorageDetailsSection";
import ExternalStorageMetricsSection from "./components/ExternalStorageMetricsSection";
import ExternalStorageNotesSection from "./components/ExternalStorageNotesSection";
import ExternalStorageOriginalDataPanel from "./components/ExternalStorageOriginalDataPanel";
import ExternalStorageSectionCollapsible from "./components/ExternalStorageSectionCollapsible";
import { ExternalStorageToolVariant } from "./externalStorage.types";
import { useExternalStorageBaseScreen } from "./hooks/useExternalStorageBaseScreen";

interface ExternalStorageBaseScreenProps {
  variant?: ExternalStorageToolVariant;
}

const ExternalStorageBaseScreen = ({ variant = "mapping" }: ExternalStorageBaseScreenProps) => {
  const screen = useExternalStorageBaseScreen({ variant });

  if (screen.isInitialLoading) {
    return (
      <div
        className="tw-flex tw-items-center tw-justify-center tw-py-12"
        aria-busy="true"
        aria-live="polite"
      >
        <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-muted-foreground" />
      </div>
    );
  }

  const facilityId = screen.watchedWarehouseName;

  return (
    <>
      <div className="tw-relative" aria-busy={screen.isSaving}>
        {screen.isSaving && (
          <div className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-background/50">
            <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-muted-foreground" />
          </div>
        )}
        <Form {...screen.form}>
          <form id="external-storage-form" onSubmit={screen.handleSubmit(screen.onSubmit)}>
            <CommonSplitContainer
            topChild={
              <div className="tw-flex tw-flex-col tw-gap-5 tw-pr-1">
                {screen.isDisconnected && (
                  <p className="tw-text-xs tw-text-muted-foreground tw-italic">
                    Connect this external warehouse to an ingredient before editing storage details.
                  </p>
                )}
                {screen.variant === "simulate" && (
                  <ExternalStorageOriginalDataPanel
                    originalData={screen.originalData}
                    searchFacilities={screen.searchFacilities}
                  />
                )}

                <ExternalStorageSectionCollapsible
                  title="Storage details"
                  expanded={screen.detailsExpanded}
                  disabled={screen.isFormDisabled}
                  onToggle={() => screen.setDetailsExpanded(prev => !prev)}
                >
                  <ExternalStorageDetailsSection
                    register={screen.register}
                    control={screen.control}
                    errors={screen.errors}
                    disabled={screen.isFormDisabled}
                    variantConfig={screen.variantConfig}
                    facilities={screen.facilities}
                    searchFacilities={screen.searchFacilities}
                    facilityLoading={screen.facilityLoading}
                    onFacilityChange={screen.handleFacilityChange}
                    onViewFacility={() => {
                      if (screen.variantConfig.usesSearchFacility) {
                        screen.setVisibleFacilityModal(true);
                      } else {
                        screen.setVisibleFacilityManager(true);
                      }
                    }}
                    nameDisabled={screen.nameDisabled}
                    selectedFacilityId={facilityId}
                  />
                </ExternalStorageSectionCollapsible>

                <ExternalStorageSectionCollapsible
                  title="Storage metrics"
                  expanded={screen.metricsExpanded}
                  disabled={screen.isFormDisabled}
                  onToggle={() => screen.setMetricsExpanded(prev => !prev)}
                >
                  <ExternalStorageMetricsSection
                    register={screen.register}
                    control={screen.control}
                    errors={screen.errors}
                    disabled={screen.isFormDisabled}
                    variantConfig={screen.variantConfig}
                    isDefaultDataType={screen.isDefaultDataType}
                  />
                </ExternalStorageSectionCollapsible>

                {screen.variantConfig.supportsNotes && (
                  <ExternalStorageSectionCollapsible
                    title="Notes & documentation"
                    expanded={screen.notesExpanded}
                    disabled={screen.lockStatus}
                    onToggle={() => screen.setNotesExpanded(prev => !prev)}
                  >
                    <ExternalStorageNotesSection
                      notesRef={screen.notesRef}
                      parentId={screen.notesParentId}
                      disabled={screen.lockStatus || !screen.notesParentId}
                    />
                  </ExternalStorageSectionCollapsible>
                )}
              </div>
            }
              bottomChild={
                <TailwindBottomButtonView
                  deleteStatus={true}
                  saveButtonStatus={true}
                  deleteButton={() => screen.setVisibleDeleteModal(true)}
                  deleteDisable={screen.lockStatus}
                  saveTitle={screen.saveTitle}
                  saveButton={() => {
                    void screen.handleSubmit(screen.onSubmit)();
                  }}
                  type="button"
                  saveDisable={screen.saveDisabled || screen.isSaving}
                  saveLoading={screen.isSaving}
                customHeight={72}
                customBottomContentMain="!tw-px-2 tw-py-3"
                testId="external-storage-bottom-button-view"
              />
            }
            bottomClassName="tw-border-t tw-border-border tw-bg-background"
          />
        </form>
      </Form>
      </div>

      {screen.visibleDeleteModal &&
        createPortal(
          <ConfirmModal
            viewModal={screen.visibleDeleteModal}
            closeModal={() => screen.setVisibleDeleteModal(false)}
            cancel={() => screen.setVisibleDeleteModal(false)}
            approve={screen.handleDelete}
            title={screen.variantConfig.deleteTitle}
            subTitle={screen.variantConfig.deleteSubtitle}
          />,
          document.body
        )}

      {screen.showFacilityRegister && screen.variantConfig.supportsNewFacility && (
        <FacilityRegisterModal
          visibleModal={screen.showFacilityRegister}
          setVisibleModal={screen.handleFacilityModalClose}
          refetchFacilityList={screen.refetchFacilityList}
        />
      )}

      {(screen.visibleFacilityManager || screen.visibleFacilityModal) && facilityId && (
        <ExternalStorageFacilityViewModal
          isOpen={screen.visibleFacilityManager || screen.visibleFacilityModal}
          onClose={() => {
            screen.setVisibleFacilityManager(false);
            screen.setVisibleFacilityModal(false);
          }}
          facilityId={facilityId}
          variant={screen.variant}
        />
      )}
    </>
  );
};

export default ExternalStorageBaseScreen;
